import { useState, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Mail, Upload, FileSpreadsheet, Download, CheckCircle, XCircle, Loader2, X, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { checkEmails } from "../../services/crmServices/leadService";
import * as XLSX from "xlsx";

const BATCH_SIZE = 500; // emails per API call

export function CheckEmailsModal({ open, onClose }) {
    const [file, setFile] = useState(null);
    const [extractedEmails, setExtractedEmails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0); // 0-100
    const [progressText, setProgressText] = useState("");
    const [results, setResults] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);
    const abortRef = useRef(false);

    // Reset state when modal closes
    const handleClose = () => {
        abortRef.current = true;
        setFile(null);
        setExtractedEmails([]);
        setResults(null);
        setLoading(false);
        setProgress(0);
        setProgressText("");
        setDragOver(false);
        onClose();
    };

    // Extract emails from uploaded file
    const parseFile = useCallback((uploadedFile) => {
        setFile(uploadedFile);
        setResults(null);
        setProgress(0);
        setProgressText("");

        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                let emails = [];
                const fileName = uploadedFile.name.toLowerCase();

                if (fileName.endsWith(".csv") || fileName.endsWith(".txt")) {
                    const text = e.target.result;
                    const lines = text.split(/\r?\n/);
                    const firstLine = lines[0]?.trim().toLowerCase();
                    const startIndex = (firstLine === "email" || firstLine === "e-mail" || firstLine === "emails" || firstLine === "mail") ? 1 : 0;

                    for (let i = startIndex; i < lines.length; i++) {
                        const line = lines[i].trim();
                        if (!line) continue;
                        const parts = line.split(/[,;\t]/);
                        for (const part of parts) {
                            const trimmed = part.trim().replace(/^["']|["']$/g, "");
                            if (trimmed && trimmed.includes("@") && trimmed.includes(".")) {
                                emails.push(trimmed);
                            }
                        }
                    }
                } else if (fileName.endsWith(".xlsx") || fileName.endsWith(".xls")) {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: "array" });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                    if (jsonData.length === 0) {
                        toast.error("The file appears to be empty");
                        return;
                    }

                    const headerRow = jsonData[0];
                    let emailColIndex = -1;

                    if (Array.isArray(headerRow)) {
                        emailColIndex = headerRow.findIndex(h =>
                            typeof h === "string" &&
                            /^(e[-_]?mail|email[-_]?address|mail)$/i.test(h.trim())
                        );
                    }

                    if (emailColIndex === -1) {
                        const sampleRows = jsonData.slice(0, Math.min(10, jsonData.length));
                        for (let col = 0; col < (headerRow?.length || 0); col++) {
                            let atCount = 0;
                            for (const row of sampleRows) {
                                const cell = row[col];
                                if (cell && typeof cell === "string" && cell.includes("@")) {
                                    atCount++;
                                }
                            }
                            if (atCount >= Math.min(3, sampleRows.length * 0.5)) {
                                emailColIndex = col;
                                break;
                            }
                        }
                    }

                    const startRow = emailColIndex !== -1 && headerRow && typeof headerRow[emailColIndex] === "string" &&
                        /^(e[-_]?mail|email[-_]?address|mail)$/i.test(headerRow[emailColIndex]?.trim()) ? 1 : 0;

                    if (emailColIndex === -1) {
                        emailColIndex = 0;
                    }

                    for (let i = startRow; i < jsonData.length; i++) {
                        const row = jsonData[i];
                        if (row && row[emailColIndex]) {
                            const cell = String(row[emailColIndex]).trim();
                            if (cell && cell.includes("@") && cell.includes(".")) {
                                emails.push(cell);
                            }
                        }
                    }
                } else {
                    toast.error("Unsupported file format. Please upload .xlsx, .xls, .csv, or .txt files.");
                    setFile(null);
                    return;
                }

                // De-duplicate
                const uniqueEmails = [...new Set(emails.map(e => e.toLowerCase()))].map(lower => {
                    return emails.find(e => e.toLowerCase() === lower);
                });

                if (uniqueEmails.length === 0) {
                    toast.error("No valid emails found in the file. Make sure the file contains email addresses.");
                    setFile(null);
                    return;
                }

                setExtractedEmails(uniqueEmails);
                toast.success(`Found ${uniqueEmails.length.toLocaleString()} unique emails in the file`);
            } catch (error) {
                console.error("Error parsing file:", error);
                toast.error("Failed to parse the file. Please check the format.");
                setFile(null);
            }
        };

        if (uploadedFile.name.toLowerCase().endsWith(".csv") || uploadedFile.name.toLowerCase().endsWith(".txt")) {
            reader.readAsText(uploadedFile);
        } else {
            reader.readAsArrayBuffer(uploadedFile);
        }
    }, []);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            parseFile(selectedFile);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            parseFile(droppedFile);
        }
    };

    // Check emails against database in batches with progress
    const handleCheckEmails = async () => {
        if (extractedEmails.length === 0) {
            toast.error("No emails to check");
            return;
        }

        setLoading(true);
        setProgress(0);
        abortRef.current = false;

        const totalEmails = extractedEmails.length;
        const totalBatches = Math.ceil(totalEmails / BATCH_SIZE);
        const existingEmailsSet = new Set();
        let processedCount = 0;

        try {
            for (let i = 0; i < totalBatches; i++) {
                if (abortRef.current) break;

                const batchStart = i * BATCH_SIZE;
                const batch = extractedEmails.slice(batchStart, batchStart + BATCH_SIZE);

                setProgressText(`Checking batch ${i + 1} of ${totalBatches} (${processedCount.toLocaleString()} / ${totalEmails.toLocaleString()} emails)...`);

                const response = await checkEmails(batch);

                if (response?.data?.foundEmails) {
                    response.data.foundEmails.forEach(email => existingEmailsSet.add(email.toLowerCase()));
                }

                processedCount += batch.length;
                setProgress(Math.round((processedCount / totalEmails) * 100));
            }

            if (abortRef.current) return;

            // Build final results
            const uniqueEmails = [];
            const existingEmails = [];

            extractedEmails.forEach(email => {
                if (existingEmailsSet.has(email.toLowerCase())) {
                    existingEmails.push(email);
                } else {
                    uniqueEmails.push(email);
                }
            });

            const finalResults = {
                totalChecked: totalEmails,
                uniqueCount: uniqueEmails.length,
                existingCount: existingEmails.length,
                uniqueEmails,
                existingEmails
            };

            setResults(finalResults);
            setProgress(100);
            setProgressText("");
            toast.success(
                `Check complete: ${finalResults.uniqueCount.toLocaleString()} unique, ${finalResults.existingCount.toLocaleString()} already exist`
            );
        } catch (error) {
            console.error("Error checking emails:", error);
            toast.error(error.response?.data?.message || "Failed to check emails against database");
        } finally {
            setLoading(false);
        }
    };

    // Download results as Excel
    const handleDownloadExcel = () => {
        if (!results) return;

        const workbook = XLSX.utils.book_new();

        // Sheet 1: Unique Leads
        const uniqueData = results.uniqueEmails.map(email => ({ Email: email }));
        const uniqueSheet = XLSX.utils.json_to_sheet(
            uniqueData.length > 0 ? uniqueData : [{ Email: "No unique emails found" }]
        );
        uniqueSheet["!cols"] = [{ wch: 40 }];
        XLSX.utils.book_append_sheet(workbook, uniqueSheet, "Unique Leads");

        // Sheet 2: Existing Leads
        const existingData = results.existingEmails.map(email => ({ Email: email }));
        const existingSheet = XLSX.utils.json_to_sheet(
            existingData.length > 0 ? existingData : [{ Email: "No existing leads found" }]
        );
        existingSheet["!cols"] = [{ wch: 40 }];
        XLSX.utils.book_append_sheet(workbook, existingSheet, "Existing Leads");

        const fileName = `email_check_results_${new Date().toISOString().slice(0, 10)}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        toast.success("Excel file downloaded successfully");
    };

    const handleReset = () => {
        abortRef.current = true;
        setFile(null);
        setExtractedEmails([]);
        setResults(null);
        setProgress(0);
        setProgressText("");
        setLoading(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Mail className="h-5 w-5 text-blue-600" />
                        Check Emails Against Database
                    </DialogTitle>
                    <DialogDescription>
                        Upload a file with emails from affiliates to check which ones already exist in the system.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* File Upload Area */}
                    {!results && !loading && (
                        <div
                            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                                dragOver
                                    ? "border-blue-500 bg-blue-50"
                                    : file
                                    ? "border-green-300 bg-green-50"
                                    : "border-gray-300 hover:border-gray-400"
                            }`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx,.xls,.csv,.txt"
                                onChange={handleFileChange}
                                className="hidden"
                            />
                            {file ? (
                                <div className="space-y-2">
                                    <FileSpreadsheet className="h-10 w-10 text-green-600 mx-auto" />
                                    <p className="text-sm font-medium text-green-800">{file.name}</p>
                                    <p className="text-xs text-green-600">
                                        {extractedEmails.length.toLocaleString()} emails extracted
                                    </p>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleReset();
                                        }}
                                        className="text-xs text-gray-500"
                                    >
                                        <X className="h-3 w-3 mr-1" />
                                        Change file
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <Upload className="h-10 w-10 text-gray-400 mx-auto" />
                                    <p className="text-sm text-gray-600">
                                        <span className="font-semibold text-blue-600">Click to upload</span> or drag and drop
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        Supports .xlsx, .xls, .csv, .txt files
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Progress Bar (while loading) */}
                    {loading && (
                        <div className="space-y-3 py-2">
                            <div className="flex items-center gap-2">
                                <Loader2 className="h-5 w-5 text-blue-600 animate-spin shrink-0" />
                                <p className="text-sm font-medium text-gray-700">Checking emails against database...</p>
                            </div>
                            <div className="space-y-1.5">
                                <div className="flex justify-between text-xs text-gray-600">
                                    <span>{progressText}</span>
                                    <span className="font-semibold">{progress}%</span>
                                </div>
                                <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-blue-500 rounded-full transition-all duration-300 ease-out"
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Results Summary */}
                    {results && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-3 gap-3">
                                <Card className="shadow-sm">
                                    <CardContent className="p-3 text-center">
                                        <p className="text-xs text-gray-500 font-medium">Total Checked</p>
                                        <p className="text-xl font-bold text-gray-900">
                                            {results.totalChecked.toLocaleString()}
                                        </p>
                                    </CardContent>
                                </Card>
                                <Card className="shadow-sm border-green-200 bg-green-50">
                                    <CardContent className="p-3 text-center">
                                        <div className="flex items-center justify-center gap-1 mb-1">
                                            <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                                            <p className="text-xs text-green-700 font-medium">Unique</p>
                                        </div>
                                        <p className="text-xl font-bold text-green-700">
                                            {results.uniqueCount.toLocaleString()}
                                        </p>
                                    </CardContent>
                                </Card>
                                <Card className="shadow-sm border-orange-200 bg-orange-50">
                                    <CardContent className="p-3 text-center">
                                        <div className="flex items-center justify-center gap-1 mb-1">
                                            <XCircle className="h-3.5 w-3.5 text-orange-600" />
                                            <p className="text-xs text-orange-700 font-medium">Already Exist</p>
                                        </div>
                                        <p className="text-xl font-bold text-orange-700">
                                            {results.existingCount.toLocaleString()}
                                        </p>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Percentage bar */}
                            <div className="space-y-1">
                                <div className="flex justify-between text-xs text-gray-600">
                                    <span>Unique: {((results.uniqueCount / results.totalChecked) * 100).toFixed(1)}%</span>
                                    <span>Existing: {((results.existingCount / results.totalChecked) * 100).toFixed(1)}%</span>
                                </div>
                                <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden flex">
                                    <div
                                        className="h-full bg-green-500 transition-all"
                                        style={{ width: `${(results.uniqueCount / results.totalChecked) * 100}%` }}
                                    />
                                    <div
                                        className="h-full bg-orange-400 transition-all"
                                        style={{ width: `${(results.existingCount / results.totalChecked) * 100}%` }}
                                    />
                                </div>
                            </div>

                            {/* Info note */}
                            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                                <p className="text-xs text-blue-700">
                                    The Excel file will contain 2 sheets: <strong>"Unique Leads"</strong> (emails not in your database — safe to buy) 
                                    and <strong>"Existing Leads"</strong> (emails already in your database).
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-2 sm:gap-0">
                    {results ? (
                        <>
                            <Button variant="outline" onClick={handleReset}>
                                <Upload className="h-4 w-4 mr-2" />
                                Check Another File
                            </Button>
                            <Button onClick={handleDownloadExcel} className="bg-green-600 hover:bg-green-700">
                                <Download className="h-4 w-4 mr-2" />
                                Download Excel
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="outline" onClick={handleClose} disabled={loading}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleCheckEmails}
                                disabled={extractedEmails.length === 0 || loading}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Checking...
                                    </>
                                ) : (
                                    <>
                                        <Mail className="h-4 w-4 mr-2" />
                                        Check Emails{extractedEmails.length > 0 ? ` (${extractedEmails.length.toLocaleString()})` : ""}
                                    </>
                                )}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
